import React from "react";
import { Bell, Check, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { cn } from "../../lib/utils";
import EmptyState from "./EmptyState";

const getIcon = (type) => {
  if (type === "warning" || type === "error")
    return (
      <AlertCircle
        size={16}
        className={type === "error" ? "text-danger-500" : "text-warning-500"}
        aria-hidden="true"
      />
    );
  if (type === "success")
    return (
      <CheckCircle2 size={16} className="text-success-500" aria-hidden="true" />
    );
  return <Info size={16} className="text-primary-500" aria-hidden="true" />;
};

const NotificationCenter = ({
  open,
  notifications = [],
  unreadCount = 0,
  onToggle,
  onClose,
  onMarkAllRead,
  onNotificationClick,
}) => (
  <div className="relative">
    <button
      type="button"
      onClick={onToggle}
      className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-neutral-600 transition hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white"
      aria-label={
        unreadCount > 0 ? `الإشعارات (${unreadCount} غير مقروءة)` : "الإشعارات"
      }
      aria-expanded={open}
      aria-haspopup="menu"
    >
      <Bell size={19} aria-hidden="true" />
      {unreadCount > 0 && (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-600 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-neutral-900">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
    {open && (
      <div
        className="absolute left-0 top-12 z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl dark:border-neutral-800 dark:bg-neutral-900"
        dir="rtl"
        role="menu"
        aria-label="مركز الإشعارات"
      >
        <div className="flex items-center justify-between gap-2 border-b border-neutral-100 bg-neutral-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950/60">
          <div>
            <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-50">
              مركز الإشعارات
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {unreadCount
                ? `${unreadCount} غير مقروءة`
                : "كل الإشعارات مقروءة"}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:text-primary-300 dark:hover:bg-primary-950/40"
            >
              <Check size={12} />
              تحديد الكل
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-4">
              <EmptyState
                icon={Bell}
                title="لا توجد إشعارات"
                description="سننبهك هنا عند وجود مهام أو أحداث تحتاج انتباهك."
              />
            </div>
          ) : (
            notifications.map((n, idx) => (
              <button
                type="button"
                key={n.notification_id || n.id || idx}
                onClick={() => onNotificationClick?.(n)}
                className={cn(
                  "flex w-full items-start gap-3 border-b border-neutral-100 px-4 py-3 text-start transition hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500/40 dark:border-neutral-800 dark:hover:bg-neutral-800/70",
                  !n.read && "bg-primary-50/60 dark:bg-primary-950/20",
                  n.read && "opacity-75",
                )}
                role="menuitem"
              >
                <span className="mt-0.5">{getIcon(n.type)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-neutral-900 dark:text-neutral-100">
                    {n.title}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-neutral-500 dark:text-neutral-400">
                    {n.message}
                  </span>
                  {n.created_at && (
                    <span className="mt-1 block font-mono text-[11px] tabular-nums text-neutral-400">
                      {new Date(n.created_at).toLocaleString("ar-AE", {
                        hour: "2-digit",
                        minute: "2-digit",
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  )}
                </span>
                {!n.read && (
                  <span
                    className="mt-1.5 h-2 w-2 rounded-full bg-primary-600"
                    aria-label="غير مقروء"
                  />
                )}
              </button>
            ))
          )}
        </div>
        <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-2 text-center dark:border-neutral-800 dark:bg-neutral-950/60">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-medium text-neutral-500 hover:text-primary-700 dark:hover:text-primary-300"
          >
            إغلاق
          </button>
        </div>
      </div>
    )}
  </div>
);

export default NotificationCenter;
